import { FC, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import { Field, useForm } from 'react-final-form';
import Icon, { IconNames } from 'components/Icon';
import colors from 'constants/colors';
import { ENV_CONFIG } from 'config/environment';
import { TextField } from 'components/Forms/TextField';
import { DescriptionField } from 'components/Forms/DescriptionField';
import { RadioField } from 'components/Forms/RadioField';
import { ConfirmationModal } from 'components/Modal/confirmationModal';
import { useFieldValue } from 'hooks/useFormHelpers';
import { SectionRow, createEmptySection, useDragAndDrop } from './utils';
import { LAYOUT_OPTIONS, RECOMMENDED_DIMENSIONS, showButtonOptions } from './constants';
import { UploadFileForm } from '../UploadFileForm';

type Props = {
  isPreviewVisible?: boolean;
};

export const SectionsForm: FC<Props> = ({ isPreviewVisible = false }) => {
  const { change } = useForm();
  const sections: SectionRow[] = useFieldValue('sections') || [];
  const querystring: string = useFieldValue('querystring') || '';
  const listRef = useRef<HTMLTableSectionElement | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const { draggingId, handleDragStart, handleDragOver, handleDragEnd } = useDragAndDrop(sections);

  useEffect(() => {
    if (!sections || sections.length === 0) {
      change('sections', [createEmptySection()]);
    }
  }, [sections, change]);

  const toggleCollapse = (id: string) => {
    const updated = (sections || []).map(r =>
      r.id === id ? { ...r, collapsed: !r.collapsed } : r
    );
    change('sections', updated);
  };

  const handleChange = <K extends keyof SectionRow>(id: string, key: K, value: SectionRow[K]) => {
    const updated = (sections || []).map(r => (r.id === id ? { ...r, [key]: value } : r));
    change('sections', updated);
  };

  const addSection = () => {
    change('sections', [...(sections || []), createEmptySection()]);
    setTimeout(() => {
      listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }, 0);
  };

  const confirmDelete = (id: string) => setDeleteId(id);
  const closeDeleteModal = () => setDeleteId(null);
  const deleteSection = () => {
    if (!deleteId) return;
    change(
      'sections',
      (sections || []).filter(section => section.id !== deleteId)
    );
    closeDeleteModal();
  };

  return (
    <div className="form-group">
      <ConfirmationModal
        isOpen={!!deleteId}
        handleSave={deleteSection}
        handleCancel={closeDeleteModal}
        warningMessage="Are you sure you would like to delete this section? This can't be undone."
      />
      <table className="draggable-table">
        <tbody ref={listRef}>
          {(sections || []).map((row, index) => (
            <tr
              key={row.id}
              draggable
              onDragStart={() => handleDragStart(row.id)}
              onDragOver={event => handleDragOver(event, row.id)}
              onDragEnd={handleDragEnd}
              className={classNames('row-draggable', { dragging: draggingId === row.id })}
            >
              <td className="section-td padding0">
                <div className="section-container">
                  <div className="row align-center justify-between">
                    <span className="oath-eyebrow inkBlue">{`Section ${index + 1}`}</span>
                    <div className="collapse-container">
                      <button
                        type="button"
                        className="oath-bodysmall link-text gray padding0"
                        onClick={() => toggleCollapse(row.id)}
                      >
                        {row.collapsed ? 'Show Section' : 'Collapse Section'}
                      </button>
                      <Icon name={IconNames.Menu} color={colors.gray} size={16} />
                    </div>
                  </div>

                  {!row.collapsed && (
                    <>
                      <div className="col-md-12 padding0">
                        <TextField
                          label="Title"
                          name={`sections.${index}.title`}
                          placeholder="Enter section title"
                        />
                      </div>

                      <DescriptionField
                        label="Description"
                        name={`sections.${index}.description`}
                        placeholder="Recommended 900 characters max"
                      />
                      <div className="row">
                        <div
                          className={classNames('padding0', {
                            'col-md-4': !isPreviewVisible,
                            'col-md-12': isPreviewVisible,
                          })}
                        >
                          <TextField
                            label="Button Copy (Default: Donate Now)"
                            name={`sections.${index}.button_copy`}
                            placeholder="Enter text"
                          />
                        </div>
                        <div
                          className={classNames({
                            'col-md-1': !isPreviewVisible,
                            'col-md-12': isPreviewVisible,
                          })}
                        />
                        <div
                          className={classNames('padding0 content-end', {
                            'col-md-7': !isPreviewVisible,
                            'col-md-12': isPreviewVisible,
                          })}
                        >
                          <div className="row">
                            <div className="col-md-6 padding-left0 content-end">
                              <RadioField
                                name={`sections.${index}.show_button`}
                                options={showButtonOptions}
                              />
                            </div>
                            {row.show_button && (
                              <Field name={`sections.${index}.cta_button_color`} allowNull>
                                {({ input }) => (
                                  <div className="col-md-6 padding0">
                                    <label htmlFor={`sections-${index}-cta-color-input`}>
                                      CTA Button Color
                                    </label>
                                    <input
                                      id={`sections-${index}-cta-color-input`}
                                      name={`sections-${index}-cta-color-input`}
                                      type="color"
                                      className="input-color-full"
                                      value={row.cta_button_color || colors.oathBlue}
                                      onChange={e => {
                                        input.onChange(e.target.value);
                                        handleChange(
                                          row.id,
                                          'cta_button_color',
                                          e.target.value as any
                                        );
                                      }}
                                    />
                                  </div>
                                )}
                              </Field>
                            )}
                          </div>
                        </div>
                      </div>
                      {row.show_button && (
                        <div className="row">
                          <div
                            className={classNames('padding0', {
                              'col-md-4': !isPreviewVisible,
                              'col-md-12': isPreviewVisible,
                            })}
                          >
                            <TextField
                              label="Redirect Link (Default: /donate?p=${name})"
                              name={`sections.${index}.redirect_link`}
                              placeholder="Enter URL"
                            />
                          </div>
                        </div>
                      )}
                      <div className="row">
                        <div className="col-md-12 padding0">
                          <RadioField
                            name={`sections.${index}.layout`}
                            label="Select Layout"
                            options={LAYOUT_OPTIONS}
                          />
                        </div>
                      </div>
                      <>
                        <div className="row">
                          <div
                            className={classNames('padding0', {
                              'col-md-4': !isPreviewVisible,
                              'col-md-12': isPreviewVisible,
                            })}
                          >
                            <Field name={`sections.${index}.background_color`} allowNull>
                              {({ input }) => (
                                <div className="col-md-12 padding0">
                                  <label htmlFor={`sections-${index}-bg-color-input`}>
                                    Background Color
                                  </label>
                                  <input
                                    id={`sections-${index}-bg-color-input`}
                                    name={`sections-${index}-bg-color-input`}
                                    type="color"
                                    className="input-color-full"
                                    value={row.background_color || '#FFFFFF'}
                                    onChange={e => {
                                      input.onChange(e.target.value);
                                      handleChange(
                                        row.id,
                                        'background_color',
                                        e.target.value as any
                                      );
                                    }}
                                  />
                                </div>
                              )}
                            </Field>
                          </div>
                          <div
                            className={classNames({
                              'col-md-2': !isPreviewVisible,
                              'col-md-12': isPreviewVisible,
                            })}
                          />
                          <div
                            className={classNames('padding0', {
                              'col-md-4': !isPreviewVisible,
                              'col-md-12': isPreviewVisible,
                            })}
                          >
                            <Field name={`sections.${index}.text_color`} allowNull>
                              {({ input }) => (
                                <div className="col-md-12 padding0">
                                  <label htmlFor={`sections-${index}-text-color-input`}>
                                    Text Color
                                  </label>
                                  <input
                                    id={`sections-${index}-text-color-input`}
                                    name={`sections-${index}-text-color-input`}
                                    type="color"
                                    className="input-color-full"
                                    value={row.text_color || colors.inkBlue}
                                    onChange={e => {
                                      input.onChange(e.target.value);
                                      handleChange(row.id, 'text_color', e.target.value as any);
                                    }}
                                  />
                                </div>
                              )}
                            </Field>
                          </div>
                        </div>
                        {row.layout !== 'NO_IMAGE' && (
                          <div className="row">
                            <div
                              className={classNames('padding0', {
                                'col-md-6': !isPreviewVisible,
                                'col-md-12': isPreviewVisible,
                              })}
                            >
                              <UploadFileForm
                                type="section"
                                label={RECOMMENDED_DIMENSIONS[row.layout]}
                                previewField={`sections.${index}.previewSection`}
                                hasImageField={`sections.${index}.has_image`}
                                previewUrl={
                                  querystring
                                    ? `${ENV_CONFIG().CLOUD_STORAGE.URL}/${ENV_CONFIG().CLOUD_STORAGE.SETS}/${querystring}/section-${row.id}-logo.png`
                                    : undefined
                                }
                                fileField={`sections.${index}.sectionFile`}
                                inputId={`section-image-${row.id}`}
                              />
                            </div>
                          </div>
                        )}
                      </>

                      <button
                        type="button"
                        className="oath-bodysmall link-text gray padding0"
                        onClick={() => confirmDelete(row.id)}
                      >
                        Remove Section
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="row">
        <button type="button" className="oath-bodysmall link-text gray" onClick={addSection}>
          Add New Section
        </button>
      </div>
    </div>
  );
};
